import { ConnectProto } from "@racoon/api";
import { NO_TEAM, groupByTeam } from "~/shared/lib/driver-teams";
import { TRUCK_TYPE_ORDER } from "~/shared/lib/truck-type";

import type { GanttSection, RouteItem, Vehicle } from "./gant-chart";

export type GanttLayout = { rows: Vehicle[]; items: RouteItem[]; sections?: GanttSection[] };

/** A route bar, also knowing whose it is: the driver mode lays rows out by driver. */
export type SourceItem = RouteItem & { driverKey: string; shiftVisibleToAi?: boolean };

export function byTruckType({
  trucks,
  items,
  typeOf,
  totals,
  filter,
  label,
}: {
  trucks: Vehicle[];
  items: RouteItem[];
  typeOf: (truckId: string) => ConnectProto.TruckType;
  totals: Map<ConnectProto.TruckType, number>;
  filter: ConnectProto.TruckType | "all";
  label: (type: ConnectProto.TruckType) => string;
}): GanttLayout {
  if (filter !== "all") {
    const rows = trucks.filter((v) => typeOf(v.id) === filter);
    const ids = new Set(rows.map((v) => v.id));
    return { rows, items: items.filter((it) => ids.has(it.vehicleId)) };
  }
  const sections = TRUCK_TYPE_ORDER.flatMap((type): GanttSection[] => {
    const rowIds = trucks.filter((v) => typeOf(v.id) === type).map((v) => v.id);
    if (rowIds.length === 0) return [];
    return [
      {
        id: `type-${type}`,
        label: label(type),
        badge: `${rowIds.length}/${Math.max(rowIds.length, totals.get(type) ?? 0)}`,
        rowIds,
      },
    ];
  });
  return { rows: trucks, items, sections };
}

/** A shift with no driver still gets a driver-view row, named after its truck. */
export const noDriverKey = (truckId: string | bigint) => `truck-${truckId}`;
const isNoDriverRow = (driverId: string) => driverId.startsWith("truck-");

/**
 * Driver rows under their teams (`groupByTeam`'s rules). A driver in several teams shows under
 * each, so every row and bar id is prefixed with its team: vis-timeline needs them unique.
 */
export function byTeam({
  drivers,
  items,
  teams,
  filter,
  noTeamLabel,
  activeDriverIds,
}: {
  drivers: Vehicle[];
  items: SourceItem[];
  teams: ConnectProto.Team[];
  filter: string;
  noTeamLabel: string;
  activeDriverIds?: string[];
}): GanttLayout & { truckIds: Set<string> } {
  const teamed = new Set(teams.flatMap((team) => team.users.map((user) => String(user.id))));
  const groups = groupByTeam(drivers, teams, (driver) => driver.id, noTeamLabel)
    .map((group) => ({ ...group, prefix: group.team ? String(group.team.id) : NO_TEAM }))
    .filter((group) => filter === "all" || group.prefix === filter);

  const rows: Vehicle[] = [];
  const out: RouteItem[] = [];
  const sections: GanttSection[] = [];
  const truckIds = new Set<string>();
  for (const group of groups) {
    const members = new Set(group.items.map((driver) => driver.id));
    for (const driver of group.items) {
      rows.push({ ...driver, id: `${group.prefix}|${driver.id}` });
      if (driver.truckId) truckIds.add(driver.truckId);
    }
    for (const { driverKey, ...item } of items) {
      if (!members.has(driverKey)) continue;
      truckIds.add(item.vehicleId);
      out.push({
        ...item,
        id: `${group.prefix}|${item.id}`,
        routeId: item.id,
        vehicleId: `${group.prefix}|${driverKey}`,
      });
    }
    if (!group.label) continue;
    const working = group.items.filter((driver) => !isNoDriverRow(driver.id)).length;
    const total = group.team
      ? group.team.users.length
      : activeDriverIds?.filter((id) => !teamed.has(id)).length;
    sections.push({
      id: `team-${group.prefix}`,
      label: group.label,
      badge: total === undefined ? String(working) : `${working}/${Math.max(working, total)}`,
      rowIds: group.items.map((driver) => `${group.prefix}|${driver.id}`),
    });
  }
  const grouped = filter === "all" && sections.length > 0;
  return { rows, items: out, sections: grouped ? sections : undefined, truckIds };
}

/** A route's job count for its row's badge, or none when the route doesn't touch that day. */
export function jobsOnDay(item: Pick<RouteItem, "start" | "end" | "jobCount">, day: Date) {
  const dayEnd = new Date(day).setDate(day.getDate() + 1);
  const touches = new Date(item.start).getTime() < dayEnd && new Date(item.end) > day;
  return touches ? item.jobCount : undefined;
}
